import React from "react"
import { CircularProgress, FloatingActionButton, IconMenu, IconButton, FontIcon, Toggle, RaisedButton } from "material-ui"
import autobind from "class-autobind"
import strings from "../../../localization/strings"
import ContentAdd from "material-ui/svg-icons/content/add"
import { IUserFilterDocument, EDefaultFilterValues, ISelectedFilterTemplateId, IJobFilter, IDate, IJobDocument, IJobSearch, IClientDocument, IMassJobCreation, EListCalendarViewModes } from "../../../models"
import HierarchySettingsStore from "../../../stores/HierarchySettingsStore"
import * as api from "../../../api"
import AccountStore from "../../../stores/AccountStore"
import AppStateStore from "../../../stores/AppStateStore"
import TableActionBar from "../../../components/layout/TableActionBar"
import DateRangeSelect from "../../../components/common/DateRangeSelect"
import ChooseListCalendarViewMode from "../../../components/common/ChooseListCalendarViewMode"
import DateFilterForCalendarView from "../../../components/common/DateFilterForCalendarView"
import ClientJobCalendarView from "../../../components/job/client_job_calendar_view/ClientJobCalendarView"
import ClientJobsTable from "./ClientJobsTable"
import AddClientJobDialog from "./AddClientJobDialog"
import AddClientJobSimpleDialog from "./AddClientJobSimpleDialog"
import * as jobHelper from "../../../helpers/jobHelper"
import { browserHistory } from "react-router"
import { MenuItem } from "../../../components/common/dropdownSelect/DropDownMenu"
import moment from "moment"
import * as utils from "../../../helpers/utils"
import JobFilter from "../../../components/filter_template/job/JobFilter"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import JobsViewBudgetDrawer from "../../../components/job/multi_job_budget/JobsViewBudgetDrawer"

interface IProps {
    readonly: boolean
    client: IClientDocument
}
interface IState {
    useDateFilter: boolean
    viewMode: EListCalendarViewModes
    jobs: IJobDocument[]
    selectedJobs: IJobDocument[]
    startDate: IDate
    endDate: IDate
    referenceDateForCalendarView: IDate
    searchText: string
    loading: boolean
    total: number
    offset: number
    limit: number
    openAddJobDialog: boolean
    selectedFilterTemplateId: ISelectedFilterTemplateId
    filterTemplates: IJobFilter[]
    openBudgetDrawer: boolean
}

export default class ClientJobs extends React.Component<IProps, IState> {
    constructor(props) {
        super(props)
        const useDateFilter = AccountStore.getCRMEnabled() && utils.getCookie("UseDateFilterForClientJobPage") === "true"
        this.state = {
            useDateFilter,
            viewMode: EListCalendarViewModes.ListView,
            jobs: [],
            selectedJobs: [],
            startDate: moment().startOf("month").format(),
            endDate: moment().endOf("month").format(),
            referenceDateForCalendarView: moment().format(),
            searchText: "",
            loading: false,
            total: 0,
            offset: 0,
            limit: 20,
            openAddJobDialog: false,
            selectedFilterTemplateId: EDefaultFilterValues.NONE,
            filterTemplates: [],
            openBudgetDrawer: false,
        }
        autobind(this)
    }

    componentDidMount() {
        HierarchySettingsStore.on("change", this.getJobs)
        this.getJobsAndFilters()
    }

    reloadJobsTable() {
        this.getJobs()
    }

    componentWillUnmount() {
        HierarchySettingsStore.removeListener("change", this.getJobs)
    }

    getJobsAndFilters() {
        this.setState({ loading: true }, () =>
            api.getFilters(AuthenticationStore.getLoggedInUser().Id).then(res => {
                const result = res.data as IUserFilterDocument
                const selectedFilterTemplateId = result && result.SelectedClientJobFilter ? result.SelectedClientJobFilter : EDefaultFilterValues.NONE
                const filterTemplates = result && result.ClientJobFilters ? result.ClientJobFilters : []
                this.setState({ filterTemplates, selectedFilterTemplateId }, () => this.getJobs())
            })
        )
    }

    getJobs() {
        this.state.selectedFilterTemplateId !== EDefaultFilterValues.CREATE_NEW &&
            this.setState({ loading: true }, () => {
                const page = this.state.viewMode === EListCalendarViewModes.ListView ? this.state.offset / this.state.limit : null
                const limit = this.state.viewMode === EListCalendarViewModes.ListView ? this.state.limit : null
                api.searchJobs(AccountStore.getCurrentAccount().Id, page, limit, this.getClientJobFilterData())
                    .then(res => {
                        const data = res.data as IJobSearch
                        this.setState({
                            loading: false,
                            total: data.TotalDocumentCount,
                            jobs: data.Documents,
                            selectedJobs: [],
                        })
                    })
            })
    }

    getClientJobFilterData(): IJobFilter {
        const selectedFilterTemplate = this.state.filterTemplates.find(i => i.Id === this.state.selectedFilterTemplateId)
        const useDateFilter = (selectedFilterTemplate ? (selectedFilterTemplate.CurrentJob || selectedFilterTemplate.FilterByStartEndDate) : this.state.useDateFilter)
        const startDate = useDateFilter ?
            this.state.viewMode === EListCalendarViewModes.ListView ?
                this.state.startDate :
                moment(this.state.referenceDateForCalendarView).utc(true).startOf("month").format() :
            null
        const endDate = useDateFilter ?
            this.state.viewMode === EListCalendarViewModes.ListView ?
                this.state.endDate :
                moment(this.state.referenceDateForCalendarView).utc(true).endOf("month").format() :
            null


        let clientJobFilter: IJobFilter = selectedFilterTemplate || { FilterByStartEndDate: false, CurrentJob: false } as IJobFilter
        clientJobFilter = {
            ...clientJobFilter,
            Search: this.state.searchText,
            HierarchyItemId: null,
            Today: moment().startOf("day").utc(true).format(),
            FilterByClientIds: [this.props.client.Id],
            FilterByStartEndDate: useDateFilter,
            StartDateLocal: startDate,
            EndDateLocal: endDate
        } as IJobFilter
        return clientJobFilter
    }

    handleAddJob(job: IMassJobCreation) {
        this.setState({ openAddJobDialog: false, loading: true }, () => {
            api.postClientMassJobs(job)
                .then(res => this.getJobs())
                .catch(err =>
                    (err.response.data.message) && (
                        err.response.data.message === "Cannot add over 10 jobs for free account." ?
                            AppStateStore.setFreeAccountDataLimitUpgradeDialogContent(strings.Cannot_add_over_10_jobs_for_free_account_Please_upgrade_your_account_plan_to_add_more) :
                            AppStateStore.setProgressSnackbarContent(err.response.data.message)
                    )
                )
        });
    }

    render() {
        const selectedFilterTemplate = this.state.filterTemplates.find(i => i.Id === this.state.selectedFilterTemplateId)
        return (
            <div className="detail-tab" style={{ position: "relative" }}>
                {!AccountStore.getCRMEnabled() ?
                    jobHelper.getAllowJobByHierarchy(HierarchySettingsStore.getGlobalLevelItemFilterId()) && !this.props.readonly &&
                    <FloatingActionButton className="fixed-add" secondary style={{ top: -15, right: 80, zIndex: 998 }} onTouchTap={() => this.setState({ openAddJobDialog: true })}>
                        <ContentAdd />
                    </FloatingActionButton> :
                    !this.props.readonly &&
                    <FloatingActionButton className="fixed-add" secondary style={{ top: -15, right: 80, zIndex: 998 }} onTouchTap={() => this.setState({ openAddJobDialog: true })}>
                        <ContentAdd />
                    </FloatingActionButton>
                }
                <TableActionBar
                    rowSelectionCount={this.state.selectedJobs.length}
                    singleSelectedMessage={strings.Job_Selected}
                    multipleSelectedMessage={strings.x_Job_Selected}
                    title={ jobHelper.checkJobFieldAvailable('plural') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.PluralJobLabel}` : strings.Jobs}
                    onSearch={(searchText) => this.setState({ searchText }, () => this.getJobs())}
                    reloadTable={this.reloadJobsTable}
                    searchText={this.state.searchText}
                    onDeleteClick={() =>
                        AppStateStore.setConfirmDialogContent({
                            content: strings.Are_you_sure_you_want_to_delete_the_selected_job,
                            confirm: () => {
                                AppStateStore.setProgressSnackbarContent("loading")
                                api.deleteJobs(this.state.selectedJobs.map((item) => item.Id))
                                    .then(() => {
                                        AppStateStore.setProgressSnackbarContent(strings.Successfully_deleted)
                                        this.getJobs()
                                    })
                                    .catch(e => AppStateStore.setProgressSnackbarContent(e.data.message))
                            }
                        })
                    }
                    readonly={this.props.readonly}
                />
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <JobFilter
                        isClientJob
                        filterTemplates={this.state.filterTemplates}
                        selectedFilterTemplateId={this.state.selectedFilterTemplateId}
                        handleChangeFilterTemplates={filterTemplates => this.setState({ filterTemplates })}
                        handleSelectFilterTemlate={filterId => {
                            const noneFilter = (filterId === EDefaultFilterValues.NONE || filterId === EDefaultFilterValues.CREATE_NEW)
                            this.setState({ selectedFilterTemplateId: filterId, useDateFilter: noneFilter ? this.state.useDateFilter : false }, () => {
                                api.setSelectedClientJobFilter(AuthenticationStore.getLoggedInUser().Id,
                                    noneFilter ? "" : filterId as string
                                )
                                this.getJobs()
                            })
                        }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: -10 }}>
                        <div style={{ marginRight: 45, marginTop: 7 }}>
                            <RaisedButton
                                data-intercom-target={"Clinet-Jobs-ViewBudget-Btn"}
                                secondary
                                disabled={this.state.loading}
                                label={strings.View_Budget}
                                onTouchTap={() => this.setState({ openBudgetDrawer: true })}
                            />
                        </div>
                        {(this.state.selectedFilterTemplateId === EDefaultFilterValues.NONE || this.state.selectedFilterTemplateId == EDefaultFilterValues.CREATE_NEW) &&
                            AccountStore.getCRMEnabled() &&
                            <Toggle
                                toggled={this.state.useDateFilter}
                                onToggle={(e, value) => this.setState({ useDateFilter: value, offset: 0, searchText: "" }, () => {
                                    this.getJobs()
                                    localStorage.setItem("findd_UseDateFilterForClientJobPage", value)
                                })}
                                labelPosition="left"
                                label={strings.Use_Date_Filter}
                                style={{ width: 150, marginTop: 13 }}
                            />
                        }
                        {// report not ready on backend, so let's hide it.
                            false &&
                            <IconMenu
                                iconButtonElement={
                                    <IconButton tooltip={strings.Reports}>
                                        <FontIcon className="fa fa-file-text" style={{ fontSize: 16 }} />
                                    </IconButton>
                                }
                            >
                                <MenuItem primaryText={strings.Client_Jobs_Report} leftIcon={<FontIcon className="fa fa-file-excel-o" />} />
                            </IconMenu>
                        }
                    </div>
                </div>
                {(this.state.useDateFilter || (selectedFilterTemplate && (selectedFilterTemplate.CurrentJob || selectedFilterTemplate.FilterByStartEndDate))) &&
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <div style={{ marginTop: 10 }}>
                            <ChooseListCalendarViewMode
                                viewMode={this.state.viewMode}
                                handleChange={viewMode => this.setState({ viewMode }, () => this.getJobs())}
                            />
                        </div>
                        {this.state.viewMode === EListCalendarViewModes.ListView ?
                            <div style={{ marginTop: -10 }}>
                                <DateRangeSelect
                                    startDate={this.state.startDate}
                                    endDate={this.state.endDate}
                                    handleChange={(startDate, endDate, isValid) => isValid && this.setState({ startDate, endDate }, () => this.getJobs())}
                                />
                            </div> :
                            <DateFilterForCalendarView
                                referenceDate={this.state.referenceDateForCalendarView}
                                handleUpdateDateFilter={dateFilter => this.setState({ referenceDateForCalendarView: dateFilter.StartDate }, () => this.getJobs())}
                            />
                        }
                    </div>
                }
                <div style={{ paddingTop: 15 }}>
                    {!this.state.useDateFilter || this.state.viewMode === EListCalendarViewModes.ListView ?
                        this.state.loading ?
                            <div className="spinner">
                                <CircularProgress />
                            </div> :
                            <div>
                                {!this.state.jobs.length ?
                                    <h2 style={{ textAlign: "center", marginTop: 25 }}>{jobHelper.checkJobFieldAvailable('singular') ? `No  ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel} Found`: strings.No_job_found}</h2> :
                                    <ClientJobsTable
                                        readonly={this.props.readonly}
                                        jobs={this.state.jobs}
                                        selectedJobs={this.state.selectedJobs}
                                        total={this.state.total}
                                        offset={this.state.offset}
                                        limit={this.state.limit}
                                        limitOptions={[20, 50, 100]}
                                        onPageClick={(offset) => this.setState({ offset }, () => this.getJobs())}
                                        onLimitChange={(limit) => this.setState({ offset: 0, limit }, () => this.getJobs())}
                                        handleClickRow={(jobId) => browserHistory.replace(`/job-details/${jobId}`)}
                                        handleSelectJobs={(selectedJobs) => this.setState({ selectedJobs })}
                                    />
                                }
                            </div> :
                        !this.state.loading &&
                        <ClientJobCalendarView
                            jobs={this.state.jobs}
                            refereceDate={moment(this.state.referenceDateForCalendarView).toDate()}
                        />
                    }
                </div>
                {this.state.openAddJobDialog &&
                    (AccountStore.getCRMEnabled() ?
                        <AddClientJobDialog
                            client={this.props.client}
                            handleSaveJob={this.handleAddJob}
                            closeDialog={() => this.setState({ openAddJobDialog: false })}
                        /> :
                        <AddClientJobSimpleDialog
                            client={this.props.client}
                            handleAdd={job => {
                                this.setState({ openAddJobDialog: false })
                                AppStateStore.setProgressSnackbarContent("loading")
                                api.addJob(job)
                                    .then(() => {
                                        AppStateStore.setProgressSnackbarContent(strings.Successfully_added)
                                        this.getJobs()
                                    })
                                    .catch(err =>
                                        (err.response.data.message) && (
                                            err.response.data.message === "Cannot add over 10 jobs for free account." ?
                                                AppStateStore.setFreeAccountDataLimitUpgradeDialogContent(strings.Cannot_add_over_10_jobs_for_free_account_Please_upgrade_your_account_plan_to_add_more) :
                                                AppStateStore.setProgressSnackbarContent(err.response.data.message)
                                        )
                                    )
                            }}
                            handleClose={() => this.setState({ openAddJobDialog: false })}
                        />
                    )
                }
                <JobsViewBudgetDrawer
                    open={this.state.openBudgetDrawer}
                    jobsCount={this.state.total}
                    jobFilter={this.getClientJobFilterData()}
                    handleClose={() => this.setState({ openBudgetDrawer: false })}
                />
            </div >
        )
    }
}
